// components/team/MembersList.tsx
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { BsPersonCheckFill } from "react-icons/bs";
import { FiEye, FiMoreVertical, FiRefreshCcw, FiXCircle } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RootState } from '../../app/store';
import profileplaceholder from '../../assets/placeholderprofile.png';
import {
  APIMember,
  setError,
  setLoading,
  setMembers,
  setRawInvitationsData,
  setRawMembersData,
  updateInvitationStatus,
  updateMemberRole,
  updateMemberStatus
} from '../../features/members/membersSlice';
import RolesSection from './RolesSection';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL } from '../../utils/constants';
import { decodeAuthToken, getAuthContext } from '../../utils/jwt';
import CheckRolesPermissionModal from './CheckRolesPermissionModal';
import InvitationsPage from './InvitationsPage';
import InviteMemberModal from './InviteMemberModal';
import OwnershipRequestsModal from './OwnershipRequestsModal';
import TransferOwnershipModal from './TransferOwnershipModal';
import UpdateRoleModal from './UpdateRoleModal';

// Types for API response
type ProfileResponse = {
  status: string;
  data: {
    tenantId: string;
    userId: string;
    email: string;
    role: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    companyName: string | null;
    companyType: string | null;
    website: string | null;
    country: string | null;
    city: string | null;
    address: string | null;
    postalCode: string | null;
    photoUrl: string | null;
    photoAssetId: string | null;
    updatedAt: string;
  };
};

type Role = {
  id: string;
  tenantId: string;
  name: string;
  systemRole: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
  version: number;
  archived: boolean;
  deletedAt: string | null;
};

type RolesResponse = {
  roles: Role[];
};

type OwnershipRequest = {
  id: string;
  tenantId: string;
  requestedByUserId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  updatedAt: string;
};

type OwnerResponse = {
  updatedAt: string;
  ownerUserId: string;
};

// Typecast icon components for React compatibility
const AiOutlineExclamationCircleComponent =
  AiOutlineExclamationCircle as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;

const FiEyeComponent = FiEye as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const BsPersonCheckFillComponent = BsPersonCheckFill as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiMoreVerticalComponent =
  FiMoreVertical as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
    onClick?: React.MouseEventHandler<SVGElement>;
  }>;

const FiXCircleComponent = FiXCircle as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FiRefreshCcwComponent = FiRefreshCcw as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const statusClasses: Record<string, string> = {
  Active: 'bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm',
  Revoke: 'bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm',
  Pending: 'bg-yellow-100 text-yellow-600 px-3 py-1 rounded-full text-sm',
  Expired: 'bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm',
  Canceled: 'bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm',
};

// Helper functions
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const getDisplayStatus = (status: string): 'Active' | 'Revoke' | 'Pending' | 'Expired' | 'Canceled' => {
  const statusMap: Record<string, 'Active' | 'Revoke' | 'Pending' | 'Expired' | 'Canceled'> = {
    'ACTIVE': 'Active',
    'REVOKED': 'Revoke',
    'PENDING': 'Pending',
    'EXPIRED': 'Expired',
    'CANCELED': 'Canceled',
  };
  return statusMap[status] || 'Active';
};

const formatLastLogin = (lastLoginAt: string | null): string => {
  if (!lastLoginAt) return '-';

  const now = new Date();
  const loginDate = new Date(lastLoginAt);
  const diffTime = Math.abs(now.getTime() - loginDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

const formatRoleName = (roleName: string, status?: string): string => {
  if (status && status.toLowerCase() === 'pending') {
    return 'No Role';
  }
  if (!roleName || roleName === 'Deleted role' || roleName === '') {
    return 'No Role';
  }
  return roleName || 'No Role';
};

// Portal Component
const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return ReactDOM.createPortal(children, document.body);
};

// Accept Invite Modal Component
const AcceptInviteModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  email: string;
  onAccept: (token: string, displayName: string) => Promise<void>;
}> = ({ isOpen, onClose, email, onAccept }) => {
  const [token, setToken] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ token?: string; displayName?: string }>({});

  if (!isOpen) return null;

  const validate = (): boolean => {
    const newErrors: { token?: string; displayName?: string } = {};
    let isValid = true;

    if (!token.trim()) {
      newErrors.token = 'Token is required';
      isValid = false;
    }

    if (!displayName.trim()) {
      newErrors.displayName = 'Name is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onAccept(token.trim(), displayName.trim());
      setToken('');
      setDisplayName('');
      setErrors({});
      onClose();
    } catch (error) {
      // Error is handled in the parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setToken('');
    setDisplayName('');
    setErrors({});
    onClose();
  };

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[99999] flex items-center justify-center"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={handleCancel}
      >
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Accept Invite</h2>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Token <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Enter your invite token"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A4BD] ${errors.token ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.token && (
              <p className="mt-1 text-sm text-red-500">{errors.token}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your display name"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#00A4BD] ${errors.displayName ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.displayName && (
              <p className="mt-1 text-sm text-red-500">{errors.displayName}</p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#00A4BD] text-white rounded-md hover:bg-[#00899E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Accepting...' : 'Accept Invite'}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

const MembersList: React.FC = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<'Members' | 'Invitations' | 'Roles'>('Members');
  const [currentPage, setCurrentPage] = useState(1);
  const [ownerRole, setOwnerRole] = useState<
    'Owner' | 'Admin' | 'Editor' | 'Viewer' | 'Marketer' | 'No Role'
  >('No Role');
  const [profileEmail, setProfileEmail] = useState<string>('');
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);
  const [showPopup, setShowPopup] = useState<number | null>(null);
  const [selectedMemberIndex, setSelectedMemberIndex] = useState<number | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  // const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [profileRoleId, setProfileRoleId] = useState<string | null>(null);
  // const [tenantId, setTenantId] = useState<string>('');
  const [isRoleDetermined, setIsRoleDetermined] = useState(false);
  const [jwtEmail, setJwtEmail] = useState<string>('');
  const [isAcceptInviteModalOpen, setIsAcceptInviteModalOpen] = useState(false);

  const members = useSelector((state: RootState) => state.members.list);
  const loading = useSelector((state: RootState) => state.members.loading);
  const error = useSelector((state: RootState) => state.members.error);
  const rawMembersData = useSelector((state: RootState) => state.members.rawMembersData);
  const rawInvitationsData = useSelector((state: RootState) => state.members.rawInvitationsData);

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [resetFn, setResetFn] = useState<() => void>(() => { });
  const [saveFn, setSaveFn] = useState<() => void>(() => { });

  const [isUpdateRoleModalOpen, setIsUpdateRoleModalOpen] = useState(false);
  const [selectedMemberForRoleUpdate, setSelectedMemberForRoleUpdate] = useState<{
    index: number;
    memberId: string;
    currentRoleName: string;
  } | null>(null);

  const itemsPerPage = 6;
  const profileData = useSelector((state: RootState) => state.profile);

  // State for ownership requests count
  const [ownershipRequestsCount, setOwnershipRequestsCount] = useState<number>(0);
  // const [isLoadingRequestsCount, setIsLoadingRequestsCount] = useState(false);

  // Restore isSpecialAdmin function
  const isSpecialAdmin = (): boolean => {
    const decoded = decodeAuthToken();
    return decoded?.userId === '698ae1380a62e40ad3cb43fb';
  };

  const shouldShowAdminView = (): boolean => {
    return ownerRole === 'Owner' || ownerRole === 'Admin' || isSpecialAdmin();
  };

  // const hasNoRole = (): boolean => {
  //   return ownerRole === 'No Role';
  // };

  const getEmailFromJWT = (): string => {
    try {
      const decoded = decodeAuthToken();
      return decoded?.email || '';
    } catch (error) {
      // console.error('Failed to get email from JWT:', error);
      toast.error('Failed to get email from JWT');
      return '';
    }
  };

  const getDisplayEmail = (): string => {
    if (profileEmail && profileEmail !== 'No email') {
      return profileEmail;
    }
    if (jwtEmail) {
      return jwtEmail;
    }
    return 'No email';
  };

  // Fetch the current owner of the team
  const fetchTeamOwner = async (): Promise<string | null> => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        return null;
      }

      const response = await fetch(`${BASE_URL}/v1/team/owner`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch team owner: ${response.status}`);
      }

      const data: OwnerResponse = await response.json();
      return data.ownerUserId;
    } catch (error: any) {
      // console.error('Failed to fetch team owner:', error);
      toast.error('Failed to fetch team owner');
      return null;
    }
  };

  // Fetch ownership requests count
  const fetchOwnershipRequestsCount = async () => {
    try {
      // setIsLoadingRequestsCount(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        return;
      }

      const response = await fetch(`${BASE_URL}/v1/team/ownership/requests`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ownership requests: ${response.status}`);
      }

      const data: OwnershipRequest[] = await response.json();
      const pendingCount = Array.isArray(data)
        ? data.filter(req => req.status === 'PENDING').length
        : 0;
      setOwnershipRequestsCount(pendingCount);
    } catch (error: any) {
      // console.error('Failed to fetch ownership requests count:', error);
      toast.error('Failed to fetch ownership requests count');
      setOwnershipRequestsCount(0);
    }
    // finally {
    //   setIsLoadingRequestsCount(false);
    // }
  };

  // Fetch roles from API
  const fetchRoles = async () => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${BASE_URL}/v1/team/roles`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          return;
        }
        throw new Error(`Failed to fetch roles: ${response.status}`);
      }

      const data: RolesResponse = await response.json();
      setRoles(data.roles);
    } catch (error: any) {
      // console.error('Failed to fetch roles:', error);
      toast.error('Failed to load roles');
    }
  };

  // Fetch profile data function
  const fetchProfileData = async () => {
    try {
      const { token, tenantId: authTenantId, userId } = getAuthContext();

      if (!token || !authTenantId) {
        throw new Error('Authentication required');
      }

      // setTenantId(authTenantId);

      const jwtEmailFromToken = getEmailFromJWT();
      setJwtEmail(jwtEmailFromToken);

      const response = await fetch(`${BASE_URL}/v1/profile/me`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': authTenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          setProfileEmail(jwtEmailFromToken || 'No email');
          setOwnerRole('No Role');
          setIsRoleDetermined(true);
          return;
        }
        throw new Error(`Failed to fetch profile: ${response.status}`);
      }

      const data: ProfileResponse = await response.json();
      setProfileEmail(data.data.email || jwtEmailFromToken || 'No email');

      const roleId = data.data.role;
      if (roleId) {
        setProfileRoleId(roleId);

        const savedProfileData = localStorage.getItem('profileData');
        if (savedProfileData) {
          const parsed = JSON.parse(savedProfileData);
          parsed.roleId = roleId;
          localStorage.setItem('profileData', JSON.stringify(parsed));
        } else {
          localStorage.setItem('profileData', JSON.stringify({ roleId }));
        }

        if (roles.length > 0) {
          const matchedRole = roles.find(role => role.id === roleId);
          if (matchedRole) {
            setOwnerRole(matchedRole.name as 'Owner' | 'Admin' | 'Editor' | 'Viewer' | 'Marketer' | 'No Role');
          } else {
            setOwnerRole('No Role');
          }
        }
      } else {
        setOwnerRole('No Role');
      }

      setIsRoleDetermined(true);

    } catch (error: any) {
      // console.error('Failed to fetch profile:', error);

      // Restore isSpecialAdmin fallback
      if (isSpecialAdmin()) {
        setOwnerRole('Admin');
        setProfileEmail('admin@email.com');

        const savedProfileData = localStorage.getItem('profileData');
        if (savedProfileData) {
          const parsed = JSON.parse(savedProfileData);
          parsed.roleId = 'b1f3a929-6220-48fc-ba0b-6911fa64d111';
          parsed.email = 'admin@email.com';
          localStorage.setItem('profileData', JSON.stringify(parsed));
        }
        setIsRoleDetermined(true);
        return;
      }

      const savedProfileData = localStorage.getItem('profileData');
      if (savedProfileData) {
        const parsed = JSON.parse(savedProfileData);
        setProfileEmail(parsed.email || jwtEmail || 'No email');
        if (parsed.roleId) {
          setProfileRoleId(parsed.roleId);
        }
      } else {
        setProfileEmail(jwtEmail || 'No email');
      }
      setOwnerRole('No Role');
      setIsRoleDetermined(true);
    }
  };

  // Handle Accept Invite
  const handleAcceptInvite = async (token: string, displayName: string) => {
    try {
      const { token: authToken, tenantId, userId } = getAuthContext();

      if (!authToken || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const loadingToast = toast.loading('Accepting invitation...');

      const response = await fetch(`${BASE_URL}/v1/team/invites/accept`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          email: getDisplayEmail(),
          displayName: displayName,
        }),
      });

      toast.dismiss(loadingToast);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to accept invite: ${response.status}`);
      }

      toast.success('Invitation accepted successfully!');
      await fetchProfileData();
      await fetchTeamMembers();
      setIsAcceptInviteModalOpen(false);

    } catch (error: any) {
      toast.error(error.message || 'Failed to accept invite');
      // console.error('Accept invite error:', error);
      throw error;
    }
  };

  // Handle Request Ownership
  const handleRequestOwnership = async () => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const loadingToast = toast.loading('Requesting ownership...');

      const response = await fetch(`${BASE_URL}/v1/team/ownership/request`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      toast.dismiss(loadingToast);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to request ownership: ${response.status}`);
      }

      toast.success('Ownership request submitted successfully!');

    } catch (error: any) {
      toast.error(error.message || 'Failed to request ownership');
      // console.error('Request ownership error:', error);
    }
  };

  // Fetch team members function
  const fetchTeamMembers = async () => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${BASE_URL}/v1/team/members`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // If user doesn't have permission to view members, show empty state
        if (response.status === 403) {
          dispatch(setMembers([]));
          dispatch(setRawMembersData([]));
          dispatch(setRawInvitationsData([]));
          // setIsInitialLoad(false);
          return;
        }
        throw new Error(`Failed to fetch team members: ${response.status}`);
      }

      const data = await response.json();
      const allItems = data.items || [];

      // Store ALL invitations data (including deleted roles)
      const invitations = allItems.filter((item: APIMember) => item.rowType === 'INVITE');
      dispatch(setRawInvitationsData(invitations));

      // Store members data (filtered - no deleted roles)
      const members = allItems.filter(
        (member: APIMember) => member.roleName !== 'Deleted role'
      );
      dispatch(setRawMembersData(members));

      // Format members for display
      const formattedMembers = members.map((member: APIMember) => {
        const status = getDisplayStatus(member.status);
        return {
          name: member.displayName || '-',
          email: member.email,
          role: formatRoleName(member.roleName, status),
          lastLogin: formatLastLogin(member.lastLoginAt),
          addedDate: formatDate(member.addedAt),
          status: status,
        };
      });

      dispatch(setMembers(formattedMembers));
      // setIsInitialLoad(false);
    } catch (error: any) {
      dispatch(setError(error.message || 'Failed to fetch team members'));
      // setIsInitialLoad(false);
    }
  };

  // Main function to determine user's role
  const determineUserRole = async () => {
    try {
      // First, fetch the team owner
      const ownerUserId = await fetchTeamOwner();

      // Get current user ID from auth context
      const { userId } = getAuthContext();

      // If the current user is the owner, set role to Owner
      if (ownerUserId && userId && ownerUserId === userId) {
        setOwnerRole('Owner');
        // Fetch ownership requests count for Owner
        await fetchOwnershipRequestsCount();
      } else {
        // Otherwise, use the existing logic from profile
        await fetchProfileData();
      }

      setIsRoleDetermined(true);
    } catch (error) {
      // console.error('Failed to determine user role:', error);
      toast.error('Failed to determine user role');
      // Fallback to profile data
      await fetchProfileData();
    }
  };

  // Fetch all data on component mount
  useEffect(() => {
    const fetchAllData = async () => {
      // Try to fetch roles, but don't block if it fails (non-admin users won't have access)
      try {
        await fetchRoles();
      } catch (error) {
        // console.log('Could not fetch roles (likely permission issue), continuing...');
      }
      // Determine user role first
      await determineUserRole();
    };

    fetchAllData();
  }, []);

  // Fetch ownership requests count when ownerRole changes to Owner
  useEffect(() => {
    if (ownerRole === 'Owner' && isRoleDetermined) {
      fetchOwnershipRequestsCount();
    }
  }, [ownerRole, isRoleDetermined]);

  // Update owner role when roles are loaded or profileRoleId changes
  useEffect(() => {
    // Only apply this logic if the user is NOT the owner
    // The owner is already set by determineUserRole
    if (isRoleDetermined && ownerRole !== 'Owner') {
      if (roles.length > 0 && profileRoleId) {
        const matchedRole = roles.find(role => role.id === profileRoleId);
        if (matchedRole) {
          setOwnerRole(matchedRole.name as 'Owner' | 'Admin' | 'Editor' | 'Viewer' | 'Marketer' | 'No Role');
        } else {
          setOwnerRole('No Role');
        }
      } else if (roles.length > 0 && !profileRoleId) {
        // Restore isSpecialAdmin check
        if (isSpecialAdmin()) {
          setOwnerRole('Admin');
        } else {
          setOwnerRole('No Role');
        }
      }
    }
  }, [roles, profileRoleId, isRoleDetermined, ownerRole]);

  // Fetch team members when role is determined - ALWAYS fetch members
  useEffect(() => {


    if (isRoleDetermined) {
      fetchTeamMembers();
    }
  }, [isRoleDetermined]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showPopup !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('.popup-menu') && !target.closest('.more-button')) {
          setShowPopup(null);
          setPopupPosition(null);
          setSelectedMemberIndex(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopup]);

  // Handle scroll to close popup
  useEffect(() => {
    const handleScroll = () => {
      if (showPopup !== null) {
        setShowPopup(null);
        setPopupPosition(null);
        setSelectedMemberIndex(null);
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showPopup]);

  // Handle three dots click
  const handleMoreClick = (e: React.MouseEvent, globalIndex: number) => {
    e.stopPropagation();

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const popupX = rect.right + 5;
    const popupY = rect.top;

    const viewportWidth = window.innerWidth;
    const popupWidth = 160;

    let finalX = popupX;
    if (finalX + popupWidth > viewportWidth - 10) {
      finalX = rect.left - popupWidth - 5;
    }
    if (finalX < 10) {
      finalX = 10;
    }

    setPopupPosition({
      x: finalX,
      y: popupY,
    });
    setShowPopup(showPopup === globalIndex ? null : globalIndex);
    setSelectedMemberIndex(globalIndex);
  };

  // Handle Revoke
  const handleRevoke = async () => {
    if (selectedMemberIndex === null) return;

    try {
      const { token, userId } = getAuthContext();

      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const memberData = rawMembersData[selectedMemberIndex];

      if (!memberData) {
        toast.error('Member data not found');
        return;
      }

      const loadingToast = toast.loading('Revoking member...');

      const response = await fetch(`${BASE_URL}/v1/team/members/${memberData.memberId}/revoke`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': memberData.tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      toast.dismiss(loadingToast);

      if (!response.ok) {
        throw new Error(`Failed to revoke member: ${response.status}`);
      }

      dispatch(updateMemberStatus({
        index: selectedMemberIndex,
        newStatus: 'Revoke'
      }));

      toast.success('Member revoked successfully!');

      setShowPopup(null);
      setPopupPosition(null);
      setSelectedMemberIndex(null);

    } catch (error: any) {
      toast.error(error.message || 'Failed to revoke member');
      // console.error('Revoke error:', error);
    }
  };

  // Handle Activate
  const handleActivate = async () => {
    if (selectedMemberIndex === null) return;

    try {
      const { token, userId } = getAuthContext();

      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const memberData = rawMembersData[selectedMemberIndex];

      if (!memberData) {
        toast.error('Member data not found');
        return;
      }

      const loadingToast = toast.loading('Activating member...');

      const response = await fetch(`${BASE_URL}/v1/team/members/${memberData.memberId}/activate`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': memberData.tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      toast.dismiss(loadingToast);

      if (!response.ok) {
        throw new Error(`Failed to activate member: ${response.status}`);
      }

      dispatch(updateMemberStatus({
        index: selectedMemberIndex,
        newStatus: 'Active'
      }));

      toast.success('Member activated successfully!');

      setShowPopup(null);
      setPopupPosition(null);
      setSelectedMemberIndex(null);

    } catch (error: any) {
      toast.error(error.message || 'Failed to activate member');
      // console.error('Activate error:', error);
    }
  };

  // Handle Resend Invite
  const handleResendInvite = async (globalIndex: number) => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const memberData = rawMembersData[globalIndex];

      if (!memberData) {
        toast.error('Member data not found');
        return;
      }

      if (!memberData.inviteId) {
        toast.error('No invite found for this member');
        return;
      }

      const idempotencyKey = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      const loadingToast = toast.loading('Resending invitation...');

      const response = await fetch(`${BASE_URL}/v1/team/invites/${memberData.inviteId}/resend`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Idempotency-Key': idempotencyKey,
          'Content-Type': 'application/json',
        },
      });

      toast.dismiss(loadingToast);

      if (!response.ok) {
        throw new Error(`Failed to resend invite: ${response.status}`);
      }

      toast.success('Invitation resent successfully!');

    } catch (error: any) {
      toast.error(error.message || 'Failed to resend invite');
      // console.error('Resend invite error:', error);
    }
  };

  const handleOpenUpdateRoleModal = () => {


    if (selectedMemberIndex === null) {
      // console.error('❌ selectedMemberIndex is null');
      toast.error('No member selected');
      return;
    }

    if (selectedMemberIndex >= rawMembersData.length) {
      // console.error('❌ Invalid index:', selectedMemberIndex, 'Total members:', rawMembersData.length);
      toast.error('Member not found');
      return;
    }

    const memberData = rawMembersData[selectedMemberIndex];
    if (!memberData) {
      // console.error('❌ Member data not found at index:', selectedMemberIndex);
      toast.error('Member data not found');
      return;
    }


    setSelectedMemberForRoleUpdate({
      index: selectedMemberIndex,
      memberId: memberData.memberId,
      currentRoleName: memberData.roleName || 'No Role',
    });
    setIsUpdateRoleModalOpen(true);

    setShowPopup(null);
    setPopupPosition(null);
    setSelectedMemberIndex(null);
  };

  const handleRoleUpdated = (index: number, newRoleName: string) => {

    // Update the member in the raw data
    const updatedRawMembers = [...rawMembersData];
    if (updatedRawMembers[index]) {
      updatedRawMembers[index] = {
        ...updatedRawMembers[index],
        roleName: newRoleName,
      };
      dispatch(setRawMembersData(updatedRawMembers));
    }

    // Update the member in the Redux store
    dispatch(updateMemberRole({
      index: index,
      newRole: newRoleName || 'No Role',
    }));
  };

  const savedProfileData = localStorage.getItem('profileData');
  const profilePhoto = savedProfileData
    ? JSON.parse(savedProfileData).profilePhoto
    : profileData.profilePhoto || profileplaceholder;

  const profileName = savedProfileData
    ? `${JSON.parse(savedProfileData).firstName || ''} ${JSON.parse(savedProfileData).lastName || ''}`.trim()
    : `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim();

  const totalPages = Math.ceil(members.length / itemsPerPage);
  const paginatedMembers = members.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Calculate start and end items
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, members.length);

  // Handle page change with smooth scroll
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isCheckRolesModalOpen, setIsCheckRolesModalOpen] = useState(false);
  const [isOwnershipRequestsModalOpen, setIsOwnershipRequestsModalOpen] = useState(false);

  const handleSaveWithToast = () => {
    saveFn();
    toast.success('Changes saved successfully!');
  };

  if (!isRoleDetermined) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00A4BD]"></div>
        <span className="ml-3 text-gray-500">Loading...</span>
      </div>
    );
  }

  const isAdminView = shouldShowAdminView();
  const displayEmail = getDisplayEmail();

  // Handle Cancel Invite
  const handleCancelInvite = async (globalIndex: number) => {
    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const memberData = rawMembersData[globalIndex];

      if (!memberData) {
        toast.error('Member data not found');
        return;
      }

      if (!memberData.inviteId) {
        toast.error('No invite found for this member');
        return;
      }

      const loadingToast = toast.loading('Cancelling invitation...');

      const response = await fetch(`${BASE_URL}/v1/team/invites/${memberData.inviteId}`, {
        method: 'DELETE',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      toast.dismiss(loadingToast);

      if (!response.ok) {
        throw new Error(`Failed to cancel invite: ${response.status}`);
      }

      dispatch(updateMemberStatus({
        index: globalIndex,
        newStatus: 'Canceled'
      }));

      toast.success('Invitation cancelled successfully!');

    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel invite');
      // console.error('Cancel invite error:', error);
    }
  };

  // Get the email from auth context
  const getAuthEmail = (): string => {
    try {
      const decoded = decodeAuthToken();
      return decoded?.email || '';
    } catch (error) {
      // console.error('Failed to get email from auth:', error);
      toast.error('Failed to get email from auth');
      return '';
    }
  };

  // Determine which email to display - use auth email if user is Owner, otherwise use profile email
  const displayOwnerEmail = ownerRole === 'Owner' ? getAuthEmail() : displayEmail;
  const currentUserEmail = (ownerRole === 'Owner' || ownerRole === 'Admin') ? getAuthEmail() : displayEmail;

  return (
    <div className='space-y-6'>
      {/* Owner Card */}
      <div className='bg-white rounded-md border p-6 flex justify-between items-center mt-4 border-gray-200 shadow-sm'>
        <div className='flex items-center gap-4'>
          <img
            src={profilePhoto}
            alt='Owner'
            className='w-12 h-12 rounded-full'
          />
          <div>
            <div className='flex items-center gap-2'>
              <h3 className='font-semibold'>
                {profileName || 'User'}
              </h3>
              <span className='bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full'>
                You
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <p className='text-gray-500 text-sm'>{displayOwnerEmail} | {ownerRole}</p>
            </div>
          </div>
        </div>
        {ownerRole === 'Admin' ? (
          <button
            className='border border-red-400 text-red-500 px-4 py-1.5 rounded-md text-sm hover:bg-red-50'
            onClick={handleRequestOwnership}
          >
            Request Ownership
          </button>
        ) : ownerRole === 'Owner' ? (
          <button
            className='border border-red-400 text-red-500 px-4 py-1.5 rounded-md text-sm hover:bg-red-50'
            onClick={() => setIsTransferModalOpen(true)}
          >
            Transfer Ownership
          </button>
        ) : null}
      </div>

      {/* Card with Tabs & Content */}
      <div className='bg-white rounded-md mt-4 border border-gray-200 shadow-sm p-6'>
        {/* Tabs + Action Button */}
        <div className='flex justify-between items-center mb-4'>
          <div className='flex space-x-6'>
            {isAdminView ? (
              ['Members', 'Invitations', 'Roles'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab as 'Members' | 'Invitations' | 'Roles');
                    setCurrentPage(1);
                  }}
                  className={`pb-2 font-medium text-[14px] ${activeTab === tab
                    ? 'border-b-2 border-[#00899E] text-[#00899E]'
                    : 'text-gray-500'
                    }`}
                >
                  {tab}
                </button>
              ))
            ) : (
              ['Members'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab as 'Members');
                    setCurrentPage(1);
                  }}
                  className={`pb-2 font-medium text-[14px] ${activeTab === tab
                    ? 'border-b-2 border-[#00899E] text-[#00899E]'
                    : 'text-gray-500'
                    }`}
                >
                  {tab}
                </button>
              ))
            )}
          </div>

          {activeTab === 'Members' && isAdminView ? (
            <div className='flex items-center gap-3'>
              {/* Ownership Requests link - only for Owner */}
              {ownerRole === 'Owner' && (
                <span
                  className={`inline-flex items-center gap-1 text-sm mr-4 font-medium transition-colors cursor-pointer ${ownershipRequestsCount > 0
                    ? 'text-[#00A4BD] hover:text-[#00899E]'
                    : 'text-gray-400 cursor-not-allowed'
                    }`}
                  onClick={() => {
                    if (ownershipRequestsCount > 0) {
                      setIsOwnershipRequestsModalOpen(true);
                    }
                  }}
                >
                  <BsPersonCheckFillComponent size={20} />
                  Ownership Requests
                  {ownershipRequestsCount > 0 && (
                    <span className="ml-1 bg-[#00A4BD] text-white text-xs px-2 py-0.5 rounded-full">
                      {ownershipRequestsCount}
                    </span>
                  )}
                </span>
              )}

              <button
                className='inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#00A4BD] text-[#00A4BD] bg-white text-sm'
                onClick={() => setIsCheckRolesModalOpen(true)}
              >
                <AiOutlineExclamationCircleComponent /> Check Role
              </button>

              <button
                className='inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#00A4BD] text-white text-sm'
                onClick={() => setIsInviteModalOpen(true)}
              >
                Invite Member
              </button>
            </div>
          ) : activeTab === 'Roles' ? (
            <div className='flex gap-3'>
              <button
                onClick={resetFn}
                disabled={!hasChanges}
                className={`px-4 py-2 border rounded-md text-sm ${hasChanges
                  ? 'border-[#00A4BD] text-[#00A4BD] bg-white'
                  : 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                  }`}
              >
                Reset
              </button>
              <button
                onClick={handleSaveWithToast}
                disabled={!hasChanges}
                className={`px-4 py-2 rounded-md text-sm ${hasChanges
                  ? 'bg-[#00A4BD] text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
              >
                Save Change
              </button>
            </div>
          ) : null}
        </div>

        {/* Content by Tab */}
        {activeTab === 'Members' ? (
          <>
            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A4BD]"></div>
                <span className="ml-2 text-gray-500">Loading members...</span>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md my-4">
                <p className="font-medium">Error loading members</p>
                <p className="text-sm">{error}</p>
                <button
                  onClick={fetchTeamMembers}
                  className="mt-2 text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Table or Empty State */}
            {!loading && !error && (
              <div>
                {members.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <div className="bg-gray-50 rounded-full p-4 mb-4">
                      <svg
                        className="w-12 h-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No members yet</h3>
                    <p className="text-gray-500 text-center max-w-sm mb-6">
                      {isAdminView
                        ? "Get started by inviting your first team member to collaborate on your projects."
                        : "There are currently no members in this team."}
                    </p>
                    {isAdminView && (
                      <button
                        onClick={() => setIsInviteModalOpen(true)}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-[#00A4BD] text-white text-sm hover:bg-[#00899E] transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Invite Member
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <table className='w-full text-left mt-4 border border-gray-200'>
                      <thead className='bg-gray-100'>
                        <tr
                          className='text-gray-600 text-sm border-b'
                          style={{ fontSize: '12px', color: '#657584' }}
                        >
                          <th className='px-6 py-3 font-normal'>MEMBERS</th>
                          <th className='px-6 py-3 font-normal'>ROLE</th>
                          <th className='px-6 py-3 font-normal'>LAST LOGIN</th>
                          <th className='px-6 py-3 font-normal'>ADDED DATE</th>
                          <th className='px-6 py-3 font-normal'>STATUS</th>
                          {isAdminView && (
                            <th className='px-6 py-3 font-normal justify-end flex'>
                              ACTIONS
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody style={{ color: '#3F5265', fontSize: '14px' }}>
                        {paginatedMembers.map((member, i) => {
                          const globalIndex = (currentPage - 1) * itemsPerPage + i;
                          // Check if this member's email matches the current user's email
                          const isCurrentUser = member.email === currentUserEmail;
                          // Get the memberId from rawMembersData
                          const memberId = rawMembersData[globalIndex]?.memberId;

                          return (
                            <tr
                              key={globalIndex}
                              className='border-b hover:bg-gray-50'
                            >
                              <td className='px-6 py-3'>
                                <div className='font-medium'>{member.name}</div>
                                <div className='text-sm text-gray-500'>
                                  {member.email}
                                </div>
                              </td>
                              <td className='px-6 py-3'>
                                {String(member.status).toLowerCase() === 'pending' ? 'No Role' : member.role}
                              </td>
                              <td className='px-6 py-3'>{member.lastLogin}</td>
                              <td className='px-6 py-3'>{member.addedDate}</td>
                              <td className='px-6 py-3'>
                                <span className={statusClasses[member.status]}>
                                  {member.status}
                                </span>
                              </td>
                              {isAdminView && (
                                <td className='px-6 py-3'>
                                  <div className='flex items-center justify-end space-x-3'>
                                    {member.status !== 'Pending' && (
                                      <div className='relative group'>
                                        <Link
                                          to={`/team/${globalIndex}`}
                                          state={{
                                            member,
                                            role: member.role,
                                            ownerRole,
                                            globalIndex,
                                            memberId: memberId,
                                          }}
                                        >
                                          <FiEyeComponent className='w-5 h-5 text-gray-600 cursor-pointer' />
                                          <span className='absolute -top-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap'>
                                            View Member Details
                                          </span>
                                        </Link>
                                      </div>
                                    )}

                                    {String(member.status).toLowerCase() === 'pending' && (
                                      <div
                                        className='relative group'
                                        onClick={() => handleCancelInvite(globalIndex)}
                                      >
                                        <FiXCircleComponent className='w-5 h-5 text-red-600 cursor-pointer hover:text-red-800 transition-colors' />
                                        <span className='absolute -top-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap'>
                                          Cancel Invite
                                        </span>
                                      </div>
                                    )}

                                    {String(member.status).toLowerCase() === 'canceled' && (
                                      <div
                                        className='relative group'
                                        onClick={() => handleResendInvite(globalIndex)}
                                      >
                                        <FiRefreshCcwComponent className='w-5 h-5 text-[#00A4BD] cursor-pointer' />
                                        <span className='absolute -top-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap'>
                                          Resend Invite
                                        </span>
                                      </div>
                                    )}

                                    {/* Show three dots only if member is NOT the current user */}
                                    {(member.status === 'Active' || member.status === 'Revoke') && !isCurrentUser && (
                                      <div className='relative'>
                                        <FiMoreVerticalComponent
                                          className='w-5 h-5 text-gray-500 cursor-pointer more-button'
                                          onClick={(e) => handleMoreClick(e, globalIndex)}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Modern Pagination */}
                    <div className='flex items-center justify-between mt-4 text-sm text-gray-500 border-t border-gray-200 pt-4'>
                      <span>
                        Showing {startItem} to {endItem} of {members.length} members
                      </span>

                      {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>

                          {(() => {
                            const pages = [];
                            const total = totalPages;
                            const current = currentPage;
                            const delta = 1;

                            pages.push(1);

                            let rangeStart = Math.max(2, current - delta);
                            let rangeEnd = Math.min(total - 1, current + delta);

                            if (rangeStart > 2) {
                              pages.push('...');
                            }

                            for (let i = rangeStart; i <= rangeEnd; i++) {
                              pages.push(i);
                            }

                            if (rangeEnd < total - 1) {
                              pages.push('...');
                            }

                            if (total > 1) {
                              pages.push(total);
                            }

                            return pages.map((page, index) => {
                              if (page === '...') {
                                return (
                                  <span
                                    key={`ellipsis-${index}`}
                                    className="px-3 py-2 text-sm text-gray-500"
                                  >
                                    …
                                  </span>
                                );
                              }

                              const pageNumber = page as number;
                              const isActive = pageNumber === current;

                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => handlePageChange(pageNumber)}
                                  className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                                    ? 'bg-[#00A4BD] text-white'
                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                  {pageNumber}
                                </button>
                              );
                            });
                          })()}

                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Popup menu */}
            {showPopup !== null && popupPosition && isAdminView && (
              <Portal>
                <div
                  className='popup-menu'
                  style={{
                    position: 'fixed',
                    top: popupPosition.y,
                    left: popupPosition.x,
                    zIndex: 99999,
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    minWidth: '160px',
                    padding: '4px 0',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {members[selectedMemberIndex!]?.status === 'Active' ? (
                    <>
                      <button
                        onClick={handleRevoke}
                        style={{
                          width: '100%',
                          padding: '8px 16px',
                          textAlign: 'left',
                          fontSize: '14px',
                          color: '#dc2626',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#fef2f2';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        Revoke
                      </button>
                      <button
                        onClick={handleOpenUpdateRoleModal}
                        style={{
                          width: '100%',
                          padding: '8px 16px',
                          textAlign: 'left',
                          fontSize: '14px',
                          color: '#00899E',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f0f9ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        Update Role
                      </button>
                    </>
                  ) : members[selectedMemberIndex!]?.status === 'Revoke' ? (
                    <button
                      onClick={handleActivate}
                      style={{
                        width: '100%',
                        padding: '8px 16px',
                        textAlign: 'left',
                        fontSize: '14px',
                        color: '#16a34a',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0fdf4';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      Activate
                    </button>
                  ) : null}
                </div>
              </Portal>
            )}
          </>
        ) : activeTab === 'Invitations' ? (
          <InvitationsPage
            invitations={rawInvitationsData}
            displayEmail={displayEmail}
            onCancelInvite={(index) => {
              // Update the member status in Redux (for Members tab)
              dispatch(updateMemberStatus({
                index: index,
                newStatus: 'Canceled'
              }));
              // Update the invitation status in rawInvitationsData (for Invitations tab)
              dispatch(updateInvitationStatus({
                index: index,
                newStatus: 'CANCELED'
              }));
            }}
          />
        ) : (
          <RolesSection
            ownerRole={ownerRole}
            onChangeState={(changes, reset, save) => {
              setHasChanges(changes);
              setResetFn(() => reset);
              setSaveFn(() => save);
            }}
          />
        )}
      </div>

      {/* Modals */}
      <AcceptInviteModal
        isOpen={isAcceptInviteModalOpen}
        onClose={() => setIsAcceptInviteModalOpen(false)}
        email={displayEmail}
        onAccept={handleAcceptInvite}
      />

      <TransferOwnershipModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        setOwnerRole={setOwnerRole}
      />
      <CheckRolesPermissionModal
        isOpen={isCheckRolesModalOpen}
        onClose={() => setIsCheckRolesModalOpen(false)}
      />
      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />
      <UpdateRoleModal
        isOpen={isUpdateRoleModalOpen}
        onClose={() => {
          setIsUpdateRoleModalOpen(false);
          setSelectedMemberForRoleUpdate(null);
        }}
        memberIndex={selectedMemberForRoleUpdate?.index ?? null}
        currentRoleName={selectedMemberForRoleUpdate?.currentRoleName || 'No Role'}
        memberId={selectedMemberForRoleUpdate?.memberId || ''}
        onRoleUpdated={handleRoleUpdated}
      />
      <OwnershipRequestsModal
        isOpen={isOwnershipRequestsModalOpen}
        onClose={() => {
          setIsOwnershipRequestsModalOpen(false);
        }}
        onActionComplete={() => {
          // Reload the entire page to refresh all data
          window.location.reload();
        }}
      />

    </div>
  );
};

export default MembersList;